import { checkPasswordRules, PASSWORD_RULES } from '@tankef/core';
import { CheckIcon } from '@tankef/icons';

import { cn } from '../../utils';

export interface PasswordRulesListProps {
  password: string;
  /** i18n-resolved labels. Keeps this component locale-agnostic. */
  labels: {
    minLength: string;
    uppercase: string;
    lowercase: string;
    digit: string;
    symbol: string;
  };
  className?: string;
  /** Hide rules that are already satisfied. Nice for a progressive UX. */
  hideWhenMet?: boolean;
}

/**
 * Live checklist. Rules light up green as the user types — never red (we guide,
 * we don't punish). Empty password shows all rules in a neutral tone so the
 * user sees the target before typing.
 */
export function PasswordRulesList({
  password,
  labels,
  className,
  hideWhenMet = false,
}: PasswordRulesListProps) {
  const rules = checkPasswordRules(password);

  const items: Array<{ key: string; label: string; met: boolean }> = [
    { key: 'minLength', label: labels.minLength, met: rules.minLength },
    { key: 'uppercase', label: labels.uppercase, met: rules.hasUppercase },
    { key: 'lowercase', label: labels.lowercase, met: rules.hasLowercase },
    { key: 'digit', label: labels.digit, met: rules.hasDigit },
    { key: 'symbol', label: labels.symbol, met: rules.hasSymbol },
  ];

  const visible = hideWhenMet ? items.filter(r => !r.met) : items;
  if (visible.length === 0) return null;

  return (
    <ul className={cn('flex flex-col gap-1', className)} aria-label="Password requirements">
      {visible.map(rule => (
        <li
          key={rule.key}
          className={cn(
            'flex items-center gap-2 text-xs transition-colors',
            rule.met ? 'text-tkf-fg-success' : 'text-tkf-fg-subtle'
          )}
        >
          <span
            className={cn(
              'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full transition-colors',
              rule.met ? 'bg-success-500 text-tkf-fg-on-primary' : 'bg-tkf-surface-muted'
            )}
            aria-hidden="true"
          >
            {rule.met ? <CheckIcon className="h-2.5 w-2.5" /> : null}
          </span>
          <span>{rule.label}</span>
        </li>
      ))}
    </ul>
  );
}

PasswordRulesList.rules = PASSWORD_RULES;
